import { useCallback } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import IconButton from 'fe-design-base/molecules/IconButton';
import Pill from 'fe-design-base/molecules/Pill';

import {
  approveRejectAndUpdateAvailability,
  deleteAvailabilityTemplate,
} from 'features/notifications/api';
import { BaseRequest } from 'features/notifications/components/BaseRequest';
import { useRequestAction } from 'features/notifications/hooks/useRequestAction';
import { STATUS_UI } from 'features/notifications/statuses';
import { Availability } from 'features/notifications/types';

import { toI18n } from 'util/i18n';
import { browserHistory } from 'util/router';

type AvailabilityProps = {
  request: Availability;
};

type AvailabilityStatus = keyof (typeof STATUS_UI)['availability'];

const Availability = ({ request }: AvailabilityProps) => {
  const {
    last_status_changed_at_secs,
    type,
    status,
    location,
    requester,
    availability,
    viewer_role,
  } = request;
  const statusValue = status as AvailabilityStatus;
  const pillUI = STATUS_UI.availability[statusValue];
  const {
    first_name: firstName,
    last_name: lastName,
    avatar,
  } = requester || {};
  const name = requester ? `${firstName} ${lastName}` : '';
  const { effective_date: effectiveDate, template_id: templateId } =
    availability || {};
  const { name: locationName } = location || {};

  const handleChevronClick = useCallback(
    () =>
      viewer_role === 'oam'
        ? browserHistory.push('/availabilities')
        : browserHistory.push('/user/availability'),
    [viewer_role]
  );

  const { isLoading: isApproving, handleAction: handleApprove } =
    useRequestAction({
      apiFunction: (id, args) =>
        approveRejectAndUpdateAvailability({
          templateId: id,
          status: args.status,
        }),
      itemId: templateId!,
      apiArgs: { status: 'approved' },
      successMessage: 'requests_feed.availability.approve_success',
      initiatedFrom: 'availability_request',
      buttonText: toI18n('requests_feed.actions.approve'),
    });

  const { isLoading: isDenying, handleAction: handleDeny } = useRequestAction({
    apiFunction: (id, args) =>
      approveRejectAndUpdateAvailability({
        templateId: id,
        status: args.status,
      }),
    itemId: templateId!,
    apiArgs: { status: 'rejected' },
    successMessage: 'requests_feed.availability.deny_success',
    initiatedFrom: 'availability_request',
    buttonText: toI18n('requests_feed.actions.deny'),
  });

  const { isLoading: isDeleting, handleAction: handleDelete } =
    useRequestAction({
      apiFunction: id => deleteAvailabilityTemplate(id),
      itemId: templateId!,
      successMessage: 'requests_feed.availability.delete_success',
      initiatedFrom: 'availability_request',
      buttonText: toI18n('requests_feed.actions.delete_availability'),
    });

  return (
    <BaseRequest
      name={name}
      updatedAt={last_status_changed_at_secs}
      type={type}
      avatar={avatar}
    >
      {viewer_role === 'requester' && (
        <Pill variant={pillUI?.variant}>{pillUI?.label}</Pill>
      )}
      <Box bw={2} bcolor="mono300" bradiusm p={16} row alignItemsCenter>
        <Box column grow={1}>
          <Text
            variant="bodySmBoldTight"
            i18n="requests_feed.availability.start_date"
          />
          <Text variant="bodySmTight" color="mono500">
            {effectiveDate}
          </Text>
          <Text variant="bodySmTight" color="mono500">
            {locationName}
          </Text>
        </Box>
        <IconButton
          icon="ChevronRight"
          onClick={handleChevronClick}
          label={toI18n('actions.view_details')}
        />
      </Box>
      {viewer_role === 'oam' && (
        <Box row gap={8} grow={1}>
          <Button
            variant="secondary"
            fullWidth
            onClick={handleDeny}
            disabled={isDenying || isApproving}
            isLoading={isDenying}
          >
            {toI18n('requests_feed.actions.deny')}
          </Button>
          <Button
            variant="primary"
            fullWidth
            onClick={handleApprove}
            disabled={isDenying || isApproving}
            isLoading={isApproving}
          >
            {toI18n('requests_feed.actions.approve')}
          </Button>
        </Box>
      )}
      {viewer_role === 'requester' && (
        <Button
          variant="secondaryDestructive"
          fullWidth
          onClick={handleDelete}
          disabled={isDeleting}
          isLoading={isDeleting}
        >
          {toI18n('requests_feed.actions.delete_availability')}
        </Button>
      )}
    </BaseRequest>
  );
};

export default Availability;
